import React, { useState, useEffect } from 'react';
import {
  Stack,
  Title,
  Button,
  Group,
  Table,
  Badge,
  ActionIcon,
  Paper,
  Text,
  Modal,
  Alert,
  Loader,
  Center
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconEye,
  IconPlayerPause,
  IconPlayerPlay,
  IconAlertCircle,
  IconRefresh
} from '@tabler/icons-react';
import { batchService } from '../../services/batchService';
import BatchForm from '../../components/admin/BatchForm';
import BatchDetails from '../../components/admin/BatchDetails';

const BatchManager = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  
  const [formOpened, formHandlers] = useDisclosure(false);
  const [detailsOpened, detailsHandlers] = useDisclosure(false);

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      const data = await batchService.getBatches();
      setBatches(data);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load batches',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNext = async () => {
    try {
      await batchService.createNextBatch();
      notifications.show({
        title: 'Success',
        message: 'Next batch created successfully',
        color: 'green',
      });
      loadBatches();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to create next batch',
        color: 'red',
      });
    }
  };

  const handleEdit = (batch) => {
    setSelectedBatch(batch);
    setFormMode('edit');
    formHandlers.open();
  };

  const handleView = (batch) => {
    setSelectedBatch(batch);
    detailsHandlers.open();
  };

  const handleToggleStop = async (batch) => {
    try {
      await batchService.toggleAdminStop(batch.id);
      notifications.show({
        title: 'Success',
        message: `Batch enrollment ${batch.bfc_admin_stop ? 'resumed' : 'stopped'}`,
        color: 'green',
      });
      loadBatches();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update batch',
        color: 'red',
      });
    }
  };

  const handleFormSubmit = () => {
    formHandlers.close();
    setSelectedBatch(null);
    loadBatches();
  };

  const getStatusBadge = (batch) => {
    if (batch.bfc_admin_stop) return <Badge color="red">Admin Stopped</Badge>;
    if (batch.bfc_max_batch_size_reached) return <Badge color="orange">Size Limit Reached</Badge>;
    if (batch.bfc_cap_time_reached) return <Badge color="yellow">Cap Time Reached</Badge>;
    return <Badge color="green">Active</Badge>;
  };

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={1}>Batch Manager</Title>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={loadBatches}
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setSelectedBatch(null);
              setFormMode('create');
              formHandlers.open();
            }}
          >
            Create Batch
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            variant="gradient"
            onClick={handleCreateNext}
          >
            Create Next Batch
          </Button>
        </Group>
      </Group>

      {batches.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center">
            <IconAlertCircle size={48} color="gray" />
            <Text size="lg" c="dimmed">No batches found</Text>
            <Text size="sm" c="dimmed">Create your first batch to get started</Text>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleCreateNext}
            >
              Create First Batch
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Batch #</Table.Th>
                <Table.Th>Start Time</Table.Th>
                <Table.Th>Cap Time</Table.Th>
                <Table.Th>Members</Table.Th>
                <Table.Th>Max Size</Table.Th>
                <Table.Th>Curriculum</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {batches.map((batch) => (
                <Table.Tr key={batch.id}>
                  <Table.Td>
                    <Text fw={600}>{batch.batch_number}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {new Date(batch.batch_start_time).toLocaleString('en-IN')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {new Date(batch.cap_time).toLocaleString('en-IN')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="blue">
                      {batch.members_count || 0}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{batch.max_batch_size}</Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {batch.curriculum?.title || 'Not Set'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{getStatusBadge(batch)}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="light"
                        size="sm"
                        onClick={() => handleView(batch)}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        color="blue"
                        size="sm"
                        onClick={() => handleEdit(batch)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        color={batch.bfc_admin_stop ? "green" : "red"}
                        size="sm"
                        onClick={() => handleToggleStop(batch)}
                      >
                        {batch.bfc_admin_stop ? <IconPlayerPlay size={16} /> : <IconPlayerPause size={16} />}
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Modal
        opened={formOpened}
        onClose={() => {
          formHandlers.close();
          setSelectedBatch(null);
        }}
        title={formMode === 'create' ? 'Create New Batch' : 'Edit Batch'}
        size="lg"
      >
        <BatchForm
          batch={selectedBatch}
          mode={formMode}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            formHandlers.close();
            setSelectedBatch(null);
          }}
        />
      </Modal>

      <Modal
        opened={detailsOpened}
        onClose={detailsHandlers.close}
        title="Batch Details"
        size="lg"
      >
        {selectedBatch && (
          <BatchDetails batch={selectedBatch} />
        )}
      </Modal>
    </Stack>
  );
};

export default BatchManager;